import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { PhotoProvider } from "../context/PhotoContext";
import { PlayerAvatar } from "./PlayerAvatar";
import { ShareProfile } from "./ShareProfile";
import { BadgeIcon, toneClasses } from "./badges";
import { computeRadar, winRate } from "./helpers";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from "recharts";
import { Loader2, Zap, ArrowLeft, Flame, Trophy, Award, History } from "lucide-react";

export function PublicProfile() {
  const { playerId } = useParams();
  const [data, setData] = useState(null);
  const [records, setRecords] = useState(null);
  const [elo, setElo] = useState(null);
  const [badges, setBadges] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    Promise.all([
      api.get(`/players/${playerId}/stats`),
      api.get(`/players/${playerId}/records`).catch(() => ({ data: null })),
      api.get(`/elo`).catch(() => ({ data: [] })),
      api.get(`/achievements`).catch(() => ({ data: { by_player: {} } })),
      api.get(`/players`).catch(() => ({ data: [] })),
    ])
      .then(([s, r, e, a, p]) => {
        if (!active) return;
        setData(s.data);
        setRecords(r.data);
        setElo((e.data || []).find((x) => x.player_id === playerId) || null);
        setBadges(a.data.by_player?.[playerId] || []);
        setPlayers(p.data || []);
      })
      .catch(() => active && setNotFound(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [playerId]);

  const photoMap = useMemo(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    return Object.fromEntries(
      players.filter((p) => p.has_photo).map((p) => [p.id, `${base}/api/players/${p.id}/photo?v=${p.photo_version}`])
    );
  }, [players]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F19]">
        <Loader2 className="w-10 h-10 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (notFound || !data?.stats) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#0B0F19] text-center px-6">
        <p className="text-slate-400">Profilo non disponibile.</p>
        <Link to="/" className="text-emerald-400 font-semibold uppercase text-sm tracking-wide">Vai al campionato</Link>
      </div>
    );
  }

  const s = data.stats;
  const wr = winRate(s);
  const radar = computeRadar(s);
  const deg = (wr / 100) * 360;
  const recent = [...data.history].filter((h) => h.result).slice(-5).reverse();

  return (
    <PhotoProvider value={photoMap}>
      <div className="min-h-screen bg-[#0B0F19] ap-grain" data-testid="public-profile">
        <header className="sticky top-0 z-40 ap-glass border-b border-white/10">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-slate-300 hover:text-emerald-400 transition-colors" data-testid="back-to-championship">
              <ArrowLeft className="w-4 h-4" />
              <span className="font-semibold uppercase text-xs tracking-wide">Campionato</span>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center">
                <Zap className="w-4 h-4 text-slate-950" strokeWidth={2.5} />
              </div>
              <span className="font-display font-black uppercase tracking-tight text-sm text-white">American Padel 2026</span>
            </div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          {/* Hero card */}
          <div className="ap-card p-6 flex flex-col sm:flex-row items-center gap-6">
            <div className="relative shrink-0">
              <div className="w-28 h-28 rounded-full flex items-center justify-center" style={{ background: `conic-gradient(#10B981 ${deg}deg, #1f2937 ${deg}deg)` }}>
                <div className="w-[104px] h-[104px] rounded-full bg-slate-900 flex items-center justify-center overflow-hidden">
                  <PlayerAvatar id={data.player.id} name={data.player.name} size={96} ring={false} />
                </div>
              </div>
              {s.position <= 3 && (
                <span className="absolute -top-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center font-display font-black text-sm text-slate-950" style={{ background: ["#F59E0B", "#9CA3AF", "#D97706"][s.position - 1] }}>
                  {s.position}
                </span>
              )}
            </div>
            <div className="text-center sm:text-left flex-1">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">American Padel 2026</div>
              <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl text-white leading-tight">{data.player.name}</h1>
              <div className="font-mono text-sm text-slate-400 mt-1">
                #{s.position} in classifica · {wr}% win
                {elo && <span className="text-cyan-400"> · ELO {elo.elo}</span>}
              </div>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { v: s.points, l: "Punti", c: "text-emerald-400" },
              { v: `${s.wins}-${s.losses}`, l: "V-P", c: "text-white" },
              { v: s.played, l: "Giocate", c: "text-white" },
              { v: `${s.games_diff > 0 ? "+" : ""}${s.games_diff}`, l: "Diff games", c: "text-cyan-400" },
            ].map((x) => (
              <div key={x.l} className="ap-card p-4 text-center">
                <div className={`font-mono font-black text-2xl ${x.c}`}>{x.v}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{x.l}</div>
              </div>
            ))}
          </div>

          {/* Radar + records */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="ap-card p-3 h-56">
              <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                <RadarChart data={radar} outerRadius="72%">
                  <PolarGrid stroke="#334155" />
                  <PolarAngleAxis dataKey="axis" tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 600 }} />
                  <Radar dataKey="v" stroke="#10B981" fill="#10B981" fillOpacity={0.4} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            {records && (
              <div className="grid grid-cols-2 gap-3">
                <div className="ap-card p-3 text-center flex flex-col justify-center">
                  <Flame className="w-4 h-4 text-orange-400 mx-auto mb-1" />
                  <div className="font-mono font-black text-lg text-white">{records.win_streak}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Serie V</div>
                </div>
                <div className="ap-card p-3 text-center flex flex-col justify-center">
                  <Trophy className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                  <div className="font-mono font-black text-lg text-emerald-400">{records.best_win ? records.best_win.score : "—"}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Miglior vittoria</div>
                </div>
                <div className="ap-card p-3 text-center flex flex-col justify-center">
                  <Award className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
                  <div className="font-bold text-sm text-white truncate">{records.best_partner ? records.best_partner.name : "—"}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Compagno top</div>
                </div>
                <div className="ap-card p-3 text-center flex flex-col justify-center">
                  <History className="w-4 h-4 text-rose-400 mx-auto mb-1" />
                  <div className="font-mono font-black text-lg text-rose-400">{records.worst_loss ? records.worst_loss.score : "—"}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Ko peggiore</div>
                </div>
              </div>
            )}
          </div>

          {/* Badges */}
          {badges.length > 0 && (
            <div className="ap-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <Award className="w-4 h-4 text-amber-400" />
                <span className="font-display uppercase tracking-wide text-sm text-slate-300">Riconoscimenti</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {badges.map((b) => {
                  const tone = toneClasses(b.tone);
                  return (
                    <div key={b.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${tone.chip}`}>
                      <BadgeIcon icon={b.icon} className={`w-3.5 h-3.5 ${tone.text}`} />
                      <span className="text-xs font-semibold text-slate-200">{b.title}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent matches */}
          {recent.length > 0 && (
            <div className="ap-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-amber-400" />
                <span className="font-display uppercase tracking-wide text-sm text-slate-300">Ultime partite</span>
              </div>
              <div className="space-y-2">
                {recent.map((h, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-slate-900/60 border border-slate-800">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center font-mono font-bold text-xs shrink-0 ${h.result === "V" ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>{h.result}</span>
                      <div className="text-sm text-slate-300 truncate">
                        con <span className="text-white font-semibold">{h.partner}</span> vs {h.opponents.join(" / ")}
                        <span className="text-slate-500 font-mono text-xs"> · G{h.matchday}</span>
                      </div>
                    </div>
                    <span className="font-mono font-bold text-emerald-400 shrink-0">{h.score}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Share */}
          <div className="ap-card p-5">
            <div className="font-display uppercase tracking-wide text-sm text-slate-300 mb-3">Condividi profilo</div>
            <ShareProfile playerId={data.player.id} name={data.player.name} subtitle={`#${s.position} in classifica · ${s.points} punti`} />
          </div>

          <div className="text-center pt-2 pb-8">
            <Link to="/" className="text-emerald-400 hover:text-emerald-300 font-semibold uppercase text-xs tracking-wide">Vai al campionato completo →</Link>
          </div>
        </main>
      </div>
    </PhotoProvider>
  );
}
